import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { authApi, authKeys, type AuthState } from '@/api/auth'
import { setActiveIdentity } from '@/api/identity'
import { errorMessage } from '@/api/client'
import { Button, Card, ErrorText, Input, PageSpinner } from '@/components/ui'

import { AuthLayout, RecoveryCodesList, TotpCodeInput, TotpQr } from './parts'
import { digitsOnly } from './utils'

function fragmentToken(hash: string): string {
  return new URLSearchParams(hash.replace(/^#/, '')).get('token') ?? ''
}

export function AuthActionPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const client = useQueryClient()
  const token = fragmentToken(location.hash)
  const path = location.pathname
  const [email, setEmail] = useState(new URLSearchParams(location.search).get('email') ?? '')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [repeat, setRepeat] = useState('')
  const [code, setCode] = useState('')
  const [pending, setPending] = useState('')
  const [enrollment, setEnrollment] = useState<{ totp_uri: string; totp_secret_display: string } | null>(null)
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)
  const [message, setMessage] = useState('')
  const [invite, setInvite] = useState<{ email: string; name: string; role: 'admin' | 'user' } | null>(null)
  const confirmStarted = useRef(false)
  const [previewVersion, setPreviewVersion] = useState(0)

  useEffect(() => {
    if (path !== '/invite') return
    if (!token) return
    let current = true
    void authApi.invitePreview(token).then((value) => { if (current) { setInvite(value); setName(value.name); setEmail(value.email) } })
      .catch((error: unknown) => { if (current) setMessage(errorMessage(error, 'Ссылка приглашения недействительна или истекла.')) })
    return () => { current = false }
  }, [path, token, previewVersion])
  const act = useMutation({
    mutationFn: async () => {
      if (path === '/forgot-password') return authApi.forgotPassword(email)
      if (!token) throw new Error('В ссылке нет токена. Открой письмо ещё раз или запроси новую ссылку.')
      if (path === '/invite') {
        if (password.length < 8 || password !== repeat) throw new Error('Проверь пароль: не короче 8 символов, поля должны совпадать.')
        await authApi.acceptInvite(token, password)
        return 'signed-in' as const
      }
      if (path === '/reset-password') {
        if (password.length < 8 || password !== repeat) throw new Error('Проверь пароль: не короче 8 символов, поля должны совпадать.')
        await authApi.resetPassword(token, password)
        return 'reset' as const
      }
      if (path === '/confirm-email') {
        await authApi.confirmEmail(token)
        return 'confirmed' as const
      }
      return undefined
    },
    onSuccess: async (result) => {
      if (result === 'signed-in' || result === 'confirmed') {
        const state = await authApi.state()
        await setActiveIdentity(state.user && !state.user.requires_email && !state.user.requires_totp_setup
          ? { id: state.user.id, legacy_owner: state.user.legacy_owner } : null)
        client.setQueryData<AuthState>(authKeys.state, state)
        navigate(state.user?.requires_email || state.user?.requires_totp_setup ? '/account/complete' : '/', { replace: true })
        return
      }
      if (result === 'reset') { setMessage('Пароль изменён. Теперь можно войти.'); navigate('/login', { replace: true, state: { notice: 'Пароль изменён. Войди с новым паролем.' } }); return }
      setMessage('Если адрес зарегистрирован, письмо со ссылкой уже отправлено. Проверь почту.')
    },
  })
  const runAction = act.mutate

  useEffect(() => {
    if (path !== '/confirm-email' || !token || confirmStarted.current) return
    const timer = window.setTimeout(() => {
      confirmStarted.current = true
      runAction()
    }, 0)
    return () => window.clearTimeout(timer)
  }, [path, token, runAction])

  const beginTotp = useMutation({
    mutationFn: () => authApi.beginTotp(password),
    onSuccess: (result) => { setPending(result.pending); setEnrollment(result) },
  })
  const confirmTotp = useMutation({
    mutationFn: () => authApi.completeTotpConfirm(pending, digitsOnly(code)),
    onSuccess: ({ recovery_codes }) => setRecoveryCodes(recovery_codes),
    onError: () => setCode(''),
  })
  // Identity activation remounts the offline provider. Keep one-time codes in this
  // screen until the user acknowledges them, as in the initial setup flow.
  const finishTotp = useMutation({
    mutationFn: async () => {
      const state = await authApi.state()
      await setActiveIdentity(state.user && !state.user.requires_email && !state.user.requires_totp_setup
        ? { id: state.user.id, legacy_owner: state.user.legacy_owner } : null)
      client.setQueryData<AuthState>(authKeys.state, state)
      navigate('/', { replace: true })
    },
  })
  const requestEmail = useMutation({ mutationFn: () => authApi.startEmailChange(email, password), onSuccess: () => setMessage('Письмо для подтверждения отправлено на новый адрес.') })

  if (path === '/confirm-email') {
    if (!token) return <AuthLayout title="Подтверждение адреса"><ErrorText>В ссылке нет токена. Открой письмо ещё раз.</ErrorText></AuthLayout>
    return <AuthLayout title="Подтверждение адреса"><p role="status">{act.isPending ? 'Подтверждаем адрес…' : message || errorMessage(act.error, 'Адрес подтверждён.')}</p><ErrorText error={act.error} />{act.isError ? <Button className="mt-3" onClick={() => { act.reset(); confirmStarted.current = false; window.setTimeout(() => { confirmStarted.current = true; runAction() }, 0) }}>Повторить</Button> : null}</AuthLayout>
  }

  if (path === '/account/complete') {
    if (recoveryCodes) return <AuthLayout title="Коды восстановления"><RecoveryCodesList codes={recoveryCodes} /><ErrorText error={finishTotp.error} /><Button className="mt-4" variant="primary" block loading={finishTotp.isPending} onClick={() => finishTotp.mutate()}>Продолжить</Button></AuthLayout>
    if (enrollment) return <AuthLayout title="Подключи обязательную 2FA" wide><TotpQr uri={enrollment.totp_uri} secret={enrollment.totp_secret_display} /><form className="mt-5 grid gap-4" onSubmit={(event: FormEvent) => { event.preventDefault(); confirmTotp.mutate() }}><TotpCodeInput label="Код из приложения" autoFocus value={code} onChange={(event) => setCode(event.target.value)} /><ErrorText error={confirmTotp.error} /><Button type="submit" variant="primary" loading={confirmTotp.isPending} disabled={digitsOnly(code).length !== 6}>Подтвердить</Button></form></AuthLayout>
    return <AuthLayout title="Заверши настройку аккаунта" description="Личные данные откроются после подтверждения email и обязательной настройки 2FA.">
      <div className="grid gap-5">
        <Card title="Подтверди email" subtitle="Ссылка придёт на указанный адрес."><form className="grid gap-3" onSubmit={(event: FormEvent) => { event.preventDefault(); requestEmail.mutate() }}><Input label="Email" type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} /><Input label="Текущий пароль" type="password" autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} /><Button type="submit" variant="primary" loading={requestEmail.isPending}>Отправить письмо</Button></form></Card>
        <Card title="Настрой обязательную 2FA"><form className="grid gap-3" onSubmit={(event: FormEvent) => { event.preventDefault(); beginTotp.mutate() }}><Input label="Текущий пароль" type="password" autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} /><Button type="submit" loading={beginTotp.isPending}>Настроить 2FA</Button></form></Card>
        <ErrorText error={requestEmail.error || beginTotp.error} /><p role="status" className="text-sm text-muted">{message}</p>
      </div>
    </AuthLayout>
  }

  const forgot = path === '/forgot-password'
  const inviting = path === '/invite'
  if (inviting && !invite) return <AuthLayout title="Приглашение">{token && !message ? <PageSpinner /> : <><ErrorText>{message || 'Приглашение недействительно.'}</ErrorText>{token ? <Button className="mt-3" onClick={() => { setMessage(''); setPreviewVersion((value) => value + 1) }}>Проверить ещё раз</Button> : null}<Link className="mt-3 block text-sm text-accent underline" to="/login">Ко входу</Link></>}</AuthLayout>
  return <AuthLayout title={forgot ? 'Восстановление пароля' : inviting ? 'Принять приглашение' : 'Новый пароль'} description={forgot ? 'Если аккаунт с таким email существует, мы отправим ссылку для сброса.' : undefined}>
    <form className="grid gap-4" onSubmit={(event: FormEvent) => { event.preventDefault(); act.mutate() }}>
      {forgot ? <Input label="Email" type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} /> : null}
      {inviting ? <><p className="text-sm text-muted">Приглашение для {invite?.email} · {invite?.role === 'admin' ? 'администратор' : 'пользователь'}</p><Input label="Имя" value={name} readOnly /><Input label="Пароль" type="password" autoComplete="new-password" minLength={8} required value={password} onChange={(event) => setPassword(event.target.value)} /><Input label="Повтори пароль" type="password" autoComplete="new-password" minLength={8} required value={repeat} onChange={(event) => setRepeat(event.target.value)} /></> : null}
      {!forgot && !inviting ? <><Input label="Новый пароль" type="password" autoComplete="new-password" minLength={8} required value={password} onChange={(event) => setPassword(event.target.value)} /><Input label="Повтори пароль" type="password" autoComplete="new-password" minLength={8} required value={repeat} onChange={(event) => setRepeat(event.target.value)} /></> : null}
      <ErrorText error={act.error} /><p role="status" className="text-sm text-muted">{message}</p>
      <Button type="submit" variant="primary" size="lg" block loading={act.isPending} disabled={inviting && !invite}>{forgot ? 'Отправить ссылку' : inviting ? 'Создать аккаунт' : 'Сохранить пароль'}</Button>
      {forgot ? <Link className="text-center text-sm text-accent underline focus-visible:outline" to="/login">Вернуться ко входу</Link> : null}
    </form>
  </AuthLayout>
}
